import React from 'react';
import { motion } from 'framer-motion';
import type { Campaign } from '../../redux/slices/campaignsSlice';
import { CampaignCard } from './CampaignCard';

interface CampaignProcessingViewProps {
  visibleCampaigns: Campaign[];
  newlyAddedIndex: number | null;
  progress: number;
  currentMessage: string;
  onCardClick: (campaignId: string) => void;
}

export const CampaignProcessingView: React.FC<CampaignProcessingViewProps> = ({
  visibleCampaigns,
  newlyAddedIndex,
  progress,
  currentMessage,
  onCardClick,
}) => {
  return (
    <div className="min-h-screen bg-[#121212] flex items-center justify-center p-6">
      <div className="max-w-3xl w-full">
        {/* Campaign Cards Grid */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="mb-8"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
            {[0, 1, 2, 3].map((index) => {
              const campaign = visibleCampaigns[index];
              const isNewlyAdded = index === newlyAddedIndex;
              
              return (
                <CampaignCard
                  key={index}
                  campaign={campaign}
                  index={index}
                  isNewlyAdded={isNewlyAdded}
                  onCardClick={onCardClick}
                />
              );
            })}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="space-y-6 text-center"
        >
          {/* Message */}
          <div className="mb-6">
            <p className="text-brand-primary text-sm font-normal italic">
              {currentMessage}
            </p>
          </div>

          {/* Progress Bar with centered percentage */}
          <div className="w-full max-w-md mx-auto">
            <div className="relative">
              <div className="h-4 bg-gray-800 rounded-full overflow-hidden relative">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.5, ease: "easeOut" }}
                  className="h-full bg-gradient-to-r from-brand-primary to-orange-600 rounded-full relative"
                >
                  {/* Shimmer effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-shimmer" />
                </motion.div>
                {/* Centered Percentage Text */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-white text-[10px] font-bold z-10">
                    {Math.round(progress)}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <style>{`
        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
        
        @keyframes flash {
          0%, 100% {
            box-shadow: 0 0 0 0 rgba(255, 127, 80, 0);
          }
          50% {
            box-shadow: 0 0 30px 10px rgba(255, 127, 80, 0.6);
          }
        }
        .animate-flash {
          animation: flash 1s ease-in-out;
        }
        
        @keyframes pulse-slow {
          0%, 100% {
            opacity: 0.4;
          }
          50% {
            opacity: 0.7;
          }
        }
        .animate-pulse-slow {
          animation: pulse-slow 2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};
